import { useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { formatNumber } from "src/helpers/Dashboard";
import WidgetPlaceholder from "./WidgetPlaceholder";
import KpiComparison, { type KpiComparisonData } from "./KpiComparison";
import KpiExportMenu from "./KpiExportMenu";
import WidgetExportCard from "./WidgetExportCard";
import type { KpiExportData } from "src/helpers/widgetExport";

type SparkPoint = {
  x: string;
  y: number;
  covered_count?: number;
  referral_count?: number;
  numerator?: number;
  denominator?: number;
  label?: string;
};

type SparkleData = {
  total_count?: number | string;
  label?: string;
  subtitle?: string;
  formula?: string;
  comparison?: KpiComparisonData | null;
  spark?: {
    period?: string;
    value_type?: string;
    points?: SparkPoint[];
  };
};

type SparkleWidgetProps = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  description?: string | null;
  widgetId?: string | number;
  // Rendered off-screen at a fixed size for image/PDF export.
  exportMode?: boolean;
};

// function formatWeekLabel(raw: string): string {
//   const match = raw.match(/^(\d{4})-W(\d+)$/);
//   if (match) return `W${match[2]}`;
//   return raw;
// }

const formatX = (raw: string): string => {
  const wk = raw.match(/^\d{4}-W(\d+)$/i);
  if (wk) return `W${wk[1]}`;
  const day = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (day) {
    const d = new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return raw;
};

function buildChartOptions(
  points: SparkPoint[],
  valueType?: string,
  exportMode?: boolean,
): Highcharts.Options {
  const categories = points.map((p) => formatX(p.x));
  // Carry the raw x label + underlying counts on each point so the tooltip can
  // show them (the x-axis labels aren't prominent on a compact sparkline).
  const values = points.map((p) => ({
    y: p.y,
    custom: {
      x: p.x,
      covered: p.covered_count,
      referral: p.referral_count,
      numerator: p.numerator,
      denominator: p.denominator,
      label: p.label,
    },
  }));
  const isPercent = (valueType ?? "").toLowerCase().startsWith("percent");

  return {
    chart: {
      type: "areaspline",
      height: exportMode ? 220 : 160,
      // No animation for export so the snapshot captures the final frame.
      animation: exportMode ? false : undefined,
      spacing: [10, 10, 10, 10],
      style: { fontFamily: "Outfit, sans-serif" },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    exporting: { enabled: false },
    accessibility: { enabled: false },
    xAxis: {
      categories,
      labels: { enabled: false },
      title: {
        text: "Weekly Data",
        style: { fontSize: "11px", fontWeight: "600", color: "#94a3b8" },
      },
      lineColor: "#dee2e6",
      tickLength: 0,
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        style: { fontSize: "11px", color: "#6c757d" },
        formatter() {
          const v = this.value as number;
          return isPercent ? `${v}%` : formatNumber(v);
        },
      },
      gridLineColor: "#f0f0f0",
    },
    tooltip: {
      useHTML: true,
      formatter() {
        const pt = this as unknown as {
          y: number;
          point?: {
            category?: string;
            options?: {
              custom?: {
                x?: string;
                covered?: number;
                referral?: number;
                numerator?: number;
                denominator?: number;
                label?: string;
              };
            };
          };
        };
        const c = pt.point?.options?.custom ?? {};
        const dateLabel = c.x ?? pt.point?.category ?? "";
        const val = isPercent ? `${pt.y}%` : formatNumber(pt.y);

        let counts = "";
        if (c.numerator != null && c.denominator != null) {
          const suffix = c.label ? ` ${c.label}` : "";
          counts = `<div style="font-size:11px;color:#64748b;margin-top:4px;">
                 <b style="color:#0f172a;">${formatNumber(c.numerator)}</b> of
                 <b style="color:#0f172a;">${formatNumber(c.denominator)}</b>${suffix}
               </div>`;
        } else if (c.numerator != null) {
          const suffix = c.label ? ` ${c.label}` : "";
          counts = `<div style="font-size:11px;color:#64748b;margin-top:4px;">
                ${suffix}
               </div>`;
        } else if (c.covered != null && c.referral != null) {
          counts = `<div style="font-size:11px;color:#64748b;margin-top:4px;">
                 <b style="color:#0f172a;">${formatNumber(c.covered)}</b> of
                 <b style="color:#0f172a;">${formatNumber(c.referral)}</b> referrals
               </div>`;
        }

        return `<div style="padding:3px 4px;min-width:140px;">
          <div style="font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:3px;">${dateLabel}</div>
          <div style="font-size:16px;font-weight:700;color:#0d6efd;line-height:1;">${val}</div>
          ${counts}
        </div>`;
      },
      backgroundColor: "#fff",
      borderColor: "#e2e8f0",
      borderRadius: 8,
      shadow: true,
      padding: 8,
      style: { fontSize: "12px" },
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.15,
        lineWidth: 2.5,
        marker: {
          enabled: true,
          radius: 4,
          symbol: "circle",
          fillColor: "#fff",
          lineWidth: 2,
          lineColor: undefined,
        },
        states: {
          hover: { lineWidthPlus: 0 },
        },
      },
    },
    series: [
      {
        type: "areaspline",
        name: "Cases",
        data: values,
        color: "#0d6efd",
        // Skip the draw-in animation for export so the snapshot isn't mid-frame.
        animation: exportMode ? false : undefined,
      },
    ],
  };
}

export default function SparkleWidget({
  isLoading,
  data,
  title,
  description,
  widgetId,
  exportMode,
}: SparkleWidgetProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={200} />;
  }

  const d = data as SparkleData;
  const points = d.spark?.points ?? [];

  const body = (
    <>
      <div
        className="kpi-counter-widget d-flex flex-column align-items-center text-center pt-2 pb-1"
        style={{ flexShrink: 0 }}
      >
        <p className="kpi-counter-widget__value mb-1">
          {formatNumber((d.total_count as number) ?? 0)}
        </p>
        {d.subtitle && (
          <p
            className="mb-1"
            style={{ fontSize: 11.5, color: "var(--bs-secondary-color)" }}
          >
            {d.subtitle}
          </p>
        )}
        <KpiComparison comparison={d.comparison} />
      </div>

      {points.length > 0 && (
        <div
          className="sparkle-widget__chart px-2 pb-1"
          style={{ flexShrink: 0 }}
        >
          <HighchartsReact
            highcharts={Highcharts}
            options={buildChartOptions(points, d.spark?.value_type, exportMode)}
          />
        </div>
      )}
    </>
  );

  if (exportMode) {
    return <div>{body}</div>;
  }

  return (
    <div
      ref={hostRef}
      className="sparkle-widget d-flex flex-column flex-fill"
      style={{ minHeight: 0, overflowY: "auto" }}
    >
      <KpiExportMenu
        data={d as KpiExportData}
        title={title}
        description={description}
        targetRef={hostRef}
        menuId={widgetId !== undefined ? String(widgetId) : undefined}
        renderExport={() => (
          <WidgetExportCard title={title}>
            <SparkleWidget isLoading={false} data={data} exportMode />
          </WidgetExportCard>
        )}
      />
      {body}
    </div>
  );
}
